import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { Box, Button, CircularProgress, Grid, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Mail } from '@mui/icons-material';
import { MemberEmailSearchFilter } from 'src/modules/invoice/models/memberEmailSearchFilter';
import { getMemberEmailLog } from 'src/shared/services/memberService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { MemberEmail } from '../models/memberEmail';
import { MemberEmailLogsModal } from './memberEmailLogsModal';
import { Member } from '../models/member';

type MemberEmailLogProps = {
  member: Member;
};

export const MemberEmailLog = (props: MemberEmailLogProps) => {
  const { member } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [openEmailLogs, setOpenEmailLogs] = useState<boolean>(false);
  const [emailLog, setEmailLog] = useState<MemberEmail[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const filter: MemberEmailSearchFilter = {
      pageIndex: 0,
      pageSize: 3,
      sortCol: 'mail_id',
      sortDir: 'desc',
      sent: true,
      circular_id: undefined,
      custom1: undefined,
      filter: '',
    };

    try {
      const result = await getMemberEmailLog(member.member_id || 0, filter);
      if (isMounted()) {
        setEmailLog(result.data.results);
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, member.member_id]);

  useEffect(() => {
    if (member.member_id) {
      fetchData();
    }
  }, [fetchData, member.member_id]);

  return (
    <>
      {member && member.member_id && (
        <Grid item xs={12}>
          <ContentSection
            header={t('member:emailLog.title')}
            button={
              emailLog && emailLog.length > 0 ? (
                <Button type='button' variant='outlined' size='small' endIcon={<Mail fontSize='small' />} onClick={() => setOpenEmailLogs(true)}>
                  {t('member:emailLog.title')}
                </Button>
              ) : undefined
            }
          >
            <>
              {loading ? (
                <Box sx={{ justifySelf: 'center' }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <List sx={{ mt: -2.0 }} dense aria-label='Email logs list'>
                  {emailLog && emailLog.length > 0 ? (
                    emailLog.map((log) => (
                      <ListItem
                        key={log.mail_id}
                        divider
                        sx={{
                          py: 0.5,
                          px: 1,
                        }}
                      >
                        <ListItemText
                          disableTypography
                          primary={
                            <Box display='flex' flexDirection='row' alignItems='flex-start'>
                              <Box flex={1} pr={1}>
                                <Typography variant='body1' sx={{ wordBreak: 'break-word' }}>
                                  {log.subject}
                                </Typography>
                              </Box>
                              <Typography
                                variant='body2'
                                sx={{
                                  color: 'text.secondary',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0,
                                }}
                              >
                                {DateHelper.getDateTime(log.circularExecutingTimeStamp)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary={t('member:emailLog.noEmails')}
                        primaryTypographyProps={{
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </>
          </ContentSection>
          {openEmailLogs && (
            <MemberEmailLogsModal
              modalOpen={openEmailLogs}
              onClose={() => setOpenEmailLogs(false)}
              memberId={member.member_id}
              firstName={member.firstName}
              lastName={member.lastName}
            />
          )}
        </Grid>
      )}
    </>
  );
};
